'use client';

import React from 'react';
import { ProductAggregateDocument, ProductVariantDocument } from '@/types/manage.types';
import { useCartState, useCartActions } from '@/core/application/providers/StateMachineProvider';
import { MinusCircle, PlusCircle, ShoppingBag, Trash } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

interface AddToCartButtonProps {
  product: ProductAggregateDocument;
  selectedVariant: ProductVariantDocument;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  showQuantity?: boolean;
  onClick?: () => void;
}

export const AddToCartButton: React.FC<AddToCartButtonProps> = ({ product, selectedVariant }) => {
  const { items, isLoading } = useCartState();
  const { updateItemQuantity } = useCartActions();

  const existingItem = items.find((item) => item.variant_id === selectedVariant.id);
  const isVariantInCart = !!existingItem;
  const variantQuantity = existingItem?.quantity || 0;

  return (
    <div className="flex justify-center items-center gap-3">
      {isVariantInCart && (
        <DecreaseOrDeleteButton
          isLoading={isLoading}
          product={product}
          selectedVariant={selectedVariant}
          variantQuantity={variantQuantity}
        />
      )}

      {isVariantInCart && (
        <input
          type="number"
          value={variantQuantity}
          onChange={(e) => {
            const quantity = parseInt(e.target.value);
            if (quantity > 0) {
              updateItemQuantity(selectedVariant.id, quantity);
            }
          }}
          className="w-20 text-center border-0 focus:outline-none h-full p-3"
        />
      )}

      <AddToCartOrIncrementButton
        product={product}
        selectedVariant={selectedVariant}
        isVariantInCart={isVariantInCart}
      />
    </div>
  );
};

const DecreaseOrDeleteButton: React.FC<{
  isLoading: boolean;
  product: ProductAggregateDocument;
  selectedVariant: ProductVariantDocument;
  variantQuantity: number;
}> = ({ isLoading, product: _product, selectedVariant, variantQuantity }) => {
  const { decrementItem, removeItem } = useCartActions();

  const handleDecrement = () => {
    if (variantQuantity <= 1) {
      removeItem(selectedVariant.id);
    } else {
      decrementItem(selectedVariant.id);
    }
  };

  return (
    <motion.button
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleDecrement}
      disabled={isLoading}
      className="bg-white px-3 py-2 rounded-full border-2 transition-all duration-300 
                 hover:bg-orange-600 hover:border-orange-600 hover:text-white 
                 flex items-center justify-center relative overflow-hidden"
      transition={{ duration: 0.2, ease: 'easeInOut' }}>
      <motion.span
        key={variantQuantity <= 1 ? 'trash' : 'minus'}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="flex items-center h-full">
        {variantQuantity > 1 ? <MinusCircle className="w-4 h-4" /> : <Trash className="w-4 h-4" />}
      </motion.span>
    </motion.button>
  );
};

const AddToCartOrIncrementButton: React.FC<{
  product: ProductAggregateDocument;
  selectedVariant: ProductVariantDocument;
  isVariantInCart: boolean;
  disabled?: boolean;
}> = ({ product, selectedVariant, isVariantInCart, disabled = false }) => {
  const { isLoading } = useCartState();
  const { addOrUpdateItem } = useCartActions();

  const handleAddToCart = () => {
    // Extended cart item with price and attributes for proper cart functionality
    const cartItem = {
      variant_id: selectedVariant.id,
      sku: selectedVariant.sku || '',
      product_name: product.name,
      quantity: 1,
      note: '',
      price: selectedVariant.price,
      attributes: selectedVariant.attribute_combination || [],
    };

    addOrUpdateItem(cartItem);
    toast(
      <div>
        <p>Added to cart</p>
        {product.name} - {selectedVariant.attribute_combination.map((attr) => `${attr.key}: ${attr.value}`).join(', ')}
      </div>,
      {
        position: 'top-center',
        icon: <ShoppingBag height={14} width={14} className="stroke-green-600" />,
        duration: 3000,
      },
    );
  };

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleAddToCart}
      disabled={disabled || isLoading}
      className={`bg-orange-600 hover:bg-orange-800 text-white px-4 py-2 rounded-full transition-all duration-300
                  flex items-center justify-center relative overflow-hidden cursor-pointer
                  disabled:opacity-50 disabled:cursor-not-allowed`}>
      {!isVariantInCart ? (
        <motion.span
          key="add"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center">
          Add to Cart
          <ShoppingBag className="ml-2 w-4 h-4" />
        </motion.span>
      ) : (
        <motion.span
          key="plus"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
          className="flex items-center">
          <PlusCircle className="w-4 h-4" />
        </motion.span>
      )}
    </motion.button>
  );
};
